/**
 * Reusable empty state component with inline SVG illustrations.
 * Drop-in replacement for "No items found" text blocks.
 */

interface EmptyStateProps {
  icon?:
    | "box"
    | "search"
    | "receipt"
    | "users"
    | "truck"
    | "clipboard"
    | "chart";
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const ICONS: Record<string, JSX.Element> = {
  box: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d="M8 20l24-12 24 12v24l-24 12L8 44V20z" />
      <path d="M8 20l24 12 24-12" />
      <path d="M32 32v24" />
    </svg>
  ),
  search: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <circle cx="28" cy="28" r="16" />
      <path d="M40 40l14 14" strokeLinecap="round" />
      <path d="M22 24h12M22 32h8" strokeLinecap="round" />
    </svg>
  ),
  receipt: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d="M16 8h32v48l-6-4-6 4-6-4-6 4-6-4-6 4V8h4z" />
      <path d="M24 20h16M24 28h12M24 36h8" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <circle cx="24" cy="20" r="8" />
      <path d="M10 48c0-7.7 6.3-14 14-14s14 6.3 14 14" />
      <circle cx="42" cy="22" r="6" />
      <path d="M42 34c5.5 0 10 4.5 10 10" />
    </svg>
  ),
  truck: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <rect x="4" y="16" width="36" height="24" rx="2" />
      <path d="M40 24h10l8 10v6H40V24z" />
      <circle cx="16" cy="44" r="4" />
      <circle cx="48" cy="44" r="4" />
    </svg>
  ),
  clipboard: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <rect x="14" y="8" width="36" height="48" rx="4" />
      <rect x="24" y="4" width="16" height="8" rx="2" />
      <path d="M24 24h16M24 32h12M24 40h8" strokeLinecap="round" />
    </svg>
  ),
  chart: (
    <svg
      className="w-16 h-16 text-slate-300"
      fill="none"
      viewBox="0 0 64 64"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <rect x="8" y="36" width="10" height="20" rx="2" />
      <rect x="22" y="24" width="10" height="32" rx="2" />
      <rect x="36" y="16" width="10" height="40" rx="2" />
      <rect x="50" y="28" width="10" height="28" rx="2" />
      <path d="M8 12l14 6 14-8 14 4" strokeLinecap="round" />
    </svg>
  ),
};

export function EmptyState({
  icon = "box",
  title,
  description,
  action,
  className = "",
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-16 px-6 ${className}`}
    >
      <div className="mb-4">{ICONS[icon]}</div>
      <h3 className="text-base font-semibold text-slate-700 mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-slate-500 mb-4 max-w-sm text-center">
          {description}
        </p>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
